import { useCallback, useMemo, useState } from "react";
import {
  type ImageItem,
  type ImageUploadState,
  nextSeq,
  patchItem,
  projectCommittedUrls,
  reconcileCommittedUrls,
  removeItemById,
  seedExistingItems,
  sortedItems,
} from "./imageItem";

export interface UpsertUploadingEntry {
  id: string;
  isHeic: boolean;
  file: File;
  localPreviewUrl: string;
  upload: ImageUploadState;
}

export interface ImageGalleryStore {
  /** seq 오름차순으로 정렬된 아이템 (그리드 렌더 입력). */
  items: ImageItem[];
  /** 커밋되는 image_urls (uploading 제외, seq 순서). */
  committedUrls: string[];
  /** 외부 form.image_urls 변경을 existing/uploaded 블록에 반영한다. */
  syncCommittedUrls: (urls: string[]) => void;
  /** 업로드 아이템을 추가하거나 (신규) 진행 상태를 patch (기존). */
  upsertUploading: (entry: UpsertUploadingEntry) => void;
  /** uploading → uploaded 로 승격. originalUrl 커밋, HEIC 면 webpUrl 세팅. */
  markUploaded: (id: string, result: { originalUrl: string; webpUrl?: string }) => void;
  /** 원격 이미지가 표시 가능해진 뒤 로컬 preview blob 참조를 제거한다. */
  clearLocalPreview: (id: string) => void;
  /** 아이템 제거 (uploading/uploaded/existing 무관). */
  remove: (id: string) => void;
}

/**
 * 갤러리의 단일 진실 스토어.
 *
 * existing(기존 form 이미지) + uploading(진행 중) + uploaded(이번 세션 완료) 아이템을
 * 하나의 seq 정렬 배열로 보유한다. 업로드 큐는 transport 일 뿐이며, 진행 상태는
 * `upsertUploading` 으로 이 스토어에 반영된다.
 *
 * @param initialUrls 마운트 시 existing 아이템(선행 seq 블록)으로 seed 할 form.image_urls.
 *                    useState 초기값에서 1회만 읽힌다.
 */
export function useImageGalleryStore(initialUrls: string[] = []): ImageGalleryStore {
  const [items, setItems] = useState<ImageItem[]>(() => seedExistingItems(initialUrls));

  const syncCommittedUrls = useCallback((urls: string[]) => {
    setItems((prev) => reconcileCommittedUrls(prev, urls));
  }, []);

  const upsertUploading = useCallback((entry: UpsertUploadingEntry) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.id === entry.id);
      if (existing) {
        if (
          existing.upload?.status === entry.upload.status &&
          existing.upload?.progress === entry.upload.progress &&
          existing.upload?.error === entry.upload.error
        ) {
          return prev;
        }
        return patchItem(prev, entry.id, { upload: entry.upload });
      }
      return [
        ...prev,
        {
          id: entry.id,
          seq: nextSeq(prev),
          lifecycle: "uploading" as const,
          isHeic: entry.isHeic,
          file: entry.file,
          localPreviewUrl: entry.localPreviewUrl,
          upload: entry.upload,
        },
      ];
    });
  }, []);

  const markUploaded = useCallback((id: string, result: { originalUrl: string; webpUrl?: string }) => {
    // 업로드 완료 후에도 서버 webp/original 이 실제 표시 가능해질 때까지 blob 미리보기는 유지한다.
    setItems((prev) =>
      patchItem(prev, id, {
        lifecycle: "uploaded",
        originalUrl: result.originalUrl,
        webpUrl: result.webpUrl,
        file: undefined,
        upload: undefined,
      }),
    );
  }, []);

  const clearLocalPreview = useCallback((id: string) => {
    setItems((prev) => patchItem(prev, id, { localPreviewUrl: undefined }));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => removeItemById(prev, id));
  }, []);

  const sorted = useMemo(() => sortedItems(items), [items]);
  const committedUrls = useMemo(() => projectCommittedUrls(items), [items]);

  return { items: sorted, committedUrls, syncCommittedUrls, upsertUploading, markUploaded, clearLocalPreview, remove };
}
